/**
 * @fileoverview File Upload Interface Component
 * 
 * Provides the drag-and-drop upload interface with visual feedback
 * and context-specific styling and messaging.
 * 
 * Requirements: 1.4, 1.5
 */

import React, { useState, useCallback } from 'react';
import { useModuleContext } from '../../context/ModuleContextProvider.jsx';
import { formatFileSize } from '../types.js';

/**
 * File Upload Interface Component
 * 
 * @param {Object} props - Component props
 * @param {FileUploadConfig} props.config - Upload configuration
 * @param {boolean} props.uploading - Whether upload is in progress
 * @param {function} props.onDrop - Drop event handler
 * @param {function} props.onOpenDialog - Open file dialog handler
 * @param {string} [props.testId] - Test ID
 */
export const FileUploadInterface = ({ 
  config, 
  uploading, 
  onDrop, 
  onOpenDialog, 
  testId = 'file-upload-interface' 
}) => {
  const { theme } = useModuleContext();
  const [dragActive, setDragActive] = useState(false);

  // Get context-specific messaging
  const getContextMessages = useCallback(() => {
    switch (config.context) {
      case 'admin-kb':
        return {
          title: 'Upload Knowledge Base Files',
          subtitle: 'Add documents to the knowledge base for AI agents',
          dragText: 'Drop files here to upload to knowledge base',
          clickText: 'Click to select files for knowledge base',
          description: 'Upload documents, PDFs, and text files for AI training'
        };
      
      case 'parrot-config':
        return {
          title: 'Upload Knowledge Base Files', // Updated: No more "Configuration Files" per requirements 1.1, 1.3
          subtitle: 'Add files to your Parrot\'s knowledge base',
          dragText: 'Drop knowledge base files here',
          clickText: 'Click to select knowledge base files',
          description: 'Upload documents, PDFs, and text files for your Parrot\'s knowledge' // Updated: No JSON/CSV per requirements 1.2, 1.3
        };
      
      case 'user-kb':
      default:
        return {
          title: 'Upload Knowledge Base Files', // Updated for consistency per requirements 1.1
          subtitle: 'Add files to your knowledge base',
          dragText: 'Drop files here to upload',
          clickText: 'Click to upload files',
          description: 'Upload documents to enhance your AI agent\'s knowledge'
        };
    }
  }, [config.context]);

  const messages = getContextMessages();

  /**
   * Handle drag events with enhanced visual feedback
   */
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (uploading) return;
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
      
      // Add visual feedback for valid/invalid files
      const items = Array.from(e.dataTransfer.items || []);
      const hasValidFiles = items.some(item => {
        if (item.kind === 'file') {
          const extension = item.type.split('/').pop()?.toLowerCase();
          return config.allowedTypes.includes(extension);
        }
        return false;
      });
      
      // Update drag state with validation info
      e.currentTarget.setAttribute('data-drag-valid', hasValidFiles.toString());
    } else if (e.type === 'dragleave') {
      // Only set dragActive to false if we're leaving the drop zone entirely
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
        e.currentTarget.removeAttribute('data-drag-valid');
      }
    }
  }, [uploading, config.allowedTypes]);

  /**
   * Handle drop event
   */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (uploading) return;
    
    onDrop(e);
  }, [uploading, onDrop]);

  /**
   * Handle click to open file dialog
   */
  const handleClick = useCallback(() => {
    if (!uploading) {
      onOpenDialog();
    }
  }, [uploading, onOpenDialog]);

  const isDark = theme?.mode === 'dark';

  // Get allowed types display text
  const allowedTypesText = config.allowedTypes
    .map(type => type.toUpperCase())
    .join(', ');

  return (
    <div
      className={`file-upload-interface ${
        dragActive ? 'drag-active' : ''
      } ${uploading ? 'uploading' : ''}`}
      data-testid={testId}
    >
      {/* Upload Area */}
      <div
        className={`upload-area ${
          dragActive
            ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20 scale-105 shadow-lg'
            : `border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 ${
                isDark ? 'bg-gray-700' : 'bg-gray-50'
              }`
        } ${
          uploading 
            ? 'opacity-50 cursor-not-allowed' 
            : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 hover:shadow-md'
        } border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 transform`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label={uploading ? 'Upload in progress' : `Click or drag ${config.multiple ? 'files' : 'a file'} to upload. Accepted types: ${config.allowedTypes.join(', ')}`}
        aria-describedby="upload-instructions"
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !uploading) {
            e.preventDefault();
            handleClick();
          }
        }}
        data-testid={`${testId}-drop-zone`}
      >
        <div className="upload-content space-y-4">
          {/* Upload Icon */}
          <div className={`upload-icon mx-auto h-16 w-16 ${
            isDark ? 'text-gray-400' : 'text-gray-400'
          }`}>
            {uploading ? (
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600" />
            ) : dragActive ? (
              <svg 
                className="w-full h-full animate-bounce text-teal-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 12l2 2 4-4" 
                />
              </svg>
            ) : (
              <svg 
                className="w-full h-full" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                />
              </svg>
            )}
          </div>
          
          {/* Upload Text */}
          <div className="upload-text space-y-2">
            <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {dragActive ? (
                <span className="font-medium text-teal-600">
                  {messages.dragText}
                </span>
              ) : uploading ? (
                <span className="font-medium text-blue-600">
                  Uploading files...
                </span>
              ) : (
                <>
                  <span className="font-medium text-teal-600 hover:text-teal-500">
                    {messages.clickText}
                  </span>
                  {' or drag and drop'}
                </>
              )}
            </p>
            
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {messages.description}
            </p>
            
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} space-y-1`}>
              <p>
                <strong>Allowed types:</strong> {allowedTypesText}
              </p>
              <p>
                <strong>Max size:</strong> {formatFileSize(config.maxFileSize)} per file
              </p>
              {config.multiple && (
                <p>
                  <strong>Multiple files:</strong> You can select multiple files at once
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Context-specific Help Text */}
      {config.context === 'admin-kb' && (
        <div className={`mt-4 p-3 rounded-lg ${
          isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-start space-x-2">
            <svg 
              className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <div className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-800'}`}>
              <p className="font-medium">Admin Upload</p>
              <p>Files uploaded here will be available to all users and can be assigned to any Parrot.</p>
            </div>
          </div>
        </div>
      )}

      {config.context === 'parrot-config' && (
        <div className={`mt-4 p-3 rounded-lg ${
          isDark ? 'bg-teal-900/20 border border-teal-800' : 'bg-teal-50 border border-teal-200'
        }`}>
          <div className="flex items-start space-x-2">
            <svg 
              className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <div className={`text-sm ${isDark ? 'text-teal-400' : 'text-teal-800'}`}>
              <p className="font-medium">Parrot Knowledge Base</p>
              <p>Upload documents, PDFs, and text files to provide knowledge for your Parrot's responses.</p>
            </div>
          </div>
        </div>
      )}

      {/* Accessibility Instructions */}
      <div id="upload-instructions" className="sr-only">
        <p>
          To upload files, you can either click the upload area to open a file dialog,
          or drag and drop files directly onto the upload area.
        </p>
        <p>
          Supported file types: {allowedTypesText}
        </p>
        <p>
          Maximum file size: {formatFileSize(config.maxFileSize)}
        </p>
        {config.multiple && (
          <p>Multiple file selection is supported.</p>
        )}
        <p>
          Use Tab to navigate to the upload area, then press Enter or Space to open the file dialog.
        </p>
      </div>

      {/* Live region for screen readers */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
        data-testid={`${testId}-live-region`}
      >
        {dragActive && 'Files ready to drop'}
        {uploading && 'Upload in progress'}
      </div>
    </div>
  );
};
